import dayjs from 'dayjs';
import groupBy from 'lodash/groupBy';
import map from 'lodash/map';
import sumBy from 'lodash/sumBy';
import values from 'lodash/values';

import type { FeatureMiddleware } from '@cast/api-client';
import { DATE_SIMPLE } from '@cast/constants';
import {
  demoSavingsProgress,
  demoAllocationGroupNetworkCostSummaries,
  demoAllocationGroupNetworkCostWorkloads,
  demoAllocationGroups,
  demoAllocationGroupSummaries,
  demoAllocationGroupWorkloads,
  demoClusterEfficiencyDetails,
  demoClusterEstimatedSavings,
  demoClusterEstimatedSavingsHistory,
  demoCostReport,
  demoDailyCost,
  demoWorkloadDataTransferCost,
  demoOrganizationClustersSummary,
  demoOrganizationClusterSummary,
  demoResourceUsage,
  demoWorkloadCostReport,
  demoWorkloadEfficiency,
  demoWorkloadEfficiencyDetails,
  demoRightsizingSummary,
  demoWorkloadTrafficDestinations,
  demoWorkloadTrafficDestinationsHistory,
  demoWorkloadLabelNames,
  demoWorkloadLabelValues,
  demoOrganizationClustersReport,
  demoNamespaceCostReport,
  demoWorkloadSummaries,
  demoAllocationGroupTotalCosts,
  demoAllocationGroupsEfficiencySummary,
  demoDataTransferCost,
  demoDataTransferCostSummary,
  demoAllocationGroupsWorkloadsEfficiency,
  demoWorkloadsGpuUtilizationSummary,
  demoWorkloadsGpuWasteCostImpact,
  demoReportCapabilities,
  demoWorkloadGpuUtilizationSummary,
  demoClusterAnomalies,
  demoOrganizationClusterUnscheduledPods,
  demoNamespacesTotalCosts,
  demoNamespacesCostsSummary,
  demoNamespacesNetworkCostsSummary,
  demoNamespacesNetworkTotalCosts,
  demoAllocationGroupTimedSummaries,
  demoClustersEfficiencySummary,
  demoClustersTimedEfficiency,
  demoClustersEfficiencySummaryByCluster,
  demoNodeTemplatesReport,
  demoNodeStorageReport,
} from '@cast/fixtures';
import {
  NamespaceFiltersResponse,
  SingleWorkloadCostReport,
} from '@cast/types';

import { getIsClusterDemoMode, getIsDemoMode } from '@cast/infrastructure-demo';

import {
  rejectWithErrorMessage,
  rejectDemoClusterRequest,
  handleOpDemoInterceptor,
  isSimulatedDemoClusterRequest,
} from '../utils';

export const costReport: FeatureMiddleware<'costReport'> = {
  getOrganizationClustersReport: async ({ args, next, resolve }) => {
    if (getIsClusterDemoMode()) {
      // TODO (Justas): Moved from the old implementation. Should be moved to fixtures
      try {
        let fixture = await demoOrganizationClustersReport();
        let dailyCosts = fixture.totalDailyCost?.map((item) => ({
          timestamp: dayjs.utc(item.timestamp).format(DATE_SIMPLE),
          value: item.value,
        }));
        const groupedCosts = groupBy(dailyCosts, 'timestamp');

        dailyCosts = values(
          map(groupedCosts, (group, timestamp) => ({
            timestamp,
            value: sumBy(group, (item) => parseFloat(item.value || '0')) + '',
          }))
        ).filter((item) =>
          dayjs.utc(item.timestamp).isSameOrAfter(args[0]?.startTime)
        );

        const lastDailyCostItem = dailyCosts[dailyCosts.length - 1];
        dailyCosts = [
          ...dailyCosts,
          {
            ...lastDailyCostItem,
            timestamp: dayjs
              .utc(lastDailyCostItem.timestamp)
              .add(1, 'day')
              .toISOString(),
          },
        ];

        fixture = {
          ...fixture,
          totalDailyCost: dailyCosts,
          topClustersCost: fixture.topClustersCost?.map((item) => ({
            ...item,
            items: dailyCosts,
          })),
        };
        return resolve(fixture);
      } catch (error) {
        console.error(error);
        return rejectWithErrorMessage('Something went wrong!');
      }
    }
    return next();
  },
  getOrganizationClustersDailyCosts: async ({ next, resolve }) => {
    if (getIsClusterDemoMode()) {
      const fixture = await demoDailyCost();
      return resolve(fixture);
    }
    return next();
  },
  getEstimatedSavings: async ({ next, args, resolve }) => {
    if (getIsClusterDemoMode() || isSimulatedDemoClusterRequest(args[0])) {
      const fixture = await demoClusterEstimatedSavings();
      return resolve(fixture);
    }
    return next();
  },
  getRightsizingSummary: async ({ next, resolve, args }) => {
    if (getIsClusterDemoMode() || isSimulatedDemoClusterRequest(args[0])) {
      const fixture = await demoRightsizingSummary();
      return resolve(fixture);
    }
    return next();
  },
  getCostHistory: async ({ next, resolve, args }) => {
    if (getIsClusterDemoMode() || isSimulatedDemoClusterRequest(args[0])) {
      const fixture = await demoClusterEstimatedSavingsHistory();
      return resolve(fixture);
    }
    return next();
  },
  getCostReport: async ({ args, next, resolve }) => {
    if (getIsClusterDemoMode() || isSimulatedDemoClusterRequest(args[0])) {
      const fixture = await demoCostReport(args[0]?.stepSeconds);
      return resolve({
        ...fixture,
        items: fixture.items?.filter((entry) =>
          dayjs(entry.timestamp).isBetween(
            dayjs(args[0]?.startTime),
            dayjs(args[0]?.endTime)
          )
        ),
      });
    }
    return next();
  },
  getWorkloadLabelNames: async ({ args, next, resolve }) => {
    if (getIsClusterDemoMode() || isSimulatedDemoClusterRequest(args[0])) {
      const fixture = await demoWorkloadLabelNames();
      return resolve(fixture);
    }
    return next();
  },
  getWorkloadLabelValues: async ({ args, next, resolve }) => {
    if (getIsClusterDemoMode() || isSimulatedDemoClusterRequest(args[0])) {
      const fixture = await demoWorkloadLabelValues();
      return resolve(fixture);
    }
    return next();
  },
  getNamespaces: async ({ args, next, resolve }) => {
    if (getIsClusterDemoMode() || isSimulatedDemoClusterRequest(args[0])) {
      const { clusterId, items } = await demoWorkloadCostReport(
        args[0].startTime,
        args[0].endTime
      );
      const namespaces = items?.map((item) => item.namespace || '') || [];
      const response: NamespaceFiltersResponse = {
        clusterNamespaces: [
          {
            clusterId,
            namespaces,
          },
        ],
      };
      return resolve(response);
    }
    return next();
  },
  getNamespace: async ({ args, next, resolve }) => {
    if (getIsClusterDemoMode() || isSimulatedDemoClusterRequest(args[0])) {
      const response = await demoNamespaceCostReport({
        clusterId: args[0]?.clusterId,
        namespace: args[0]?.namespace,
        start: args[0]?.startTime,
        end: args[0]?.endTime,
      });
      return resolve(response);
    }
    return next();
  },
  getNamespacesCostSummary: async ({ args, next, resolve }) => {
    if (getIsClusterDemoMode() || isSimulatedDemoClusterRequest(args[0])) {
      const response = await demoNamespacesCostsSummary();
      return resolve(response);
    }
    return next();
  },
  getNamespacesTotalCosts: async ({ args, next, resolve }) => {
    if (getIsClusterDemoMode() || isSimulatedDemoClusterRequest(args[0])) {
      const response = await demoNamespacesTotalCosts(
        args[0]?.startTime,
        args[0]?.endTime
      );
      return resolve(response);
    }
    return next();
  },
  getNamespacesDataTransferCostSummary: async ({ args, next, resolve }) => {
    if (getIsClusterDemoMode() || isSimulatedDemoClusterRequest(args[0])) {
      const response = await demoNamespacesNetworkCostsSummary();
      return resolve(response);
    }
    return next();
  },
  getNamespacesDataTransferTotalCosts: async ({ args, next, resolve }) => {
    if (getIsClusterDemoMode() || isSimulatedDemoClusterRequest(args[0])) {
      const response = await demoNamespacesNetworkTotalCosts(
        args[0]?.startTime,
        args[0]?.endTime
      );
      return resolve(response);
    }
    return next();
  },
  getWorkloadsCostReport: async ({ args, next, resolve }) => {
    if (getIsClusterDemoMode() || isSimulatedDemoClusterRequest(args[0])) {
      const fixture = await demoWorkloadCostReport(
        args[0]?.startTime,
        args[0]?.endTime,
        args[0]?.filter
      );
      const pageLimit = parseInt(args[0]?.pageLimit || '5', 10);
      return resolve({
        ...fixture,
        items: fixture.items?.slice(0, pageLimit),
      });
    }
    return next();
  },
  getWorkloadsCostSummaries: async ({ args, next, resolve }) => {
    if (getIsClusterDemoMode() || isSimulatedDemoClusterRequest(args[0])) {
      const fixture = await demoWorkloadSummaries({
        start: args[0]?.startTime,
        end: args[0]?.endTime,
        filter: args[0]?.filter,
      });
      return resolve(fixture);
    }
    return next();
  },
  getSingleWorkloadCostReport: async ({ args, next, resolve }) => {
    if (getIsClusterDemoMode() || isSimulatedDemoClusterRequest(args[0])) {
      const { clusterId, items } = await demoWorkloadCostReport(
        args[0]?.startTime,
        args[0]?.endTime
      );
      const workload = items?.find(
        (item) =>
          item.namespace === args[0]?.namespace &&
          item.workloadType === args[0]?.workloadType &&
          item.workloadName === args[0]?.workloadName
      );
      const fixture: SingleWorkloadCostReport = {
        clusterId,
        item: workload,
      };
      return resolve(fixture);
    }
    return next();
  },
  getWorkloadEfficiency: async ({ args, next, resolve }) => {
    if (getIsClusterDemoMode() || isSimulatedDemoClusterRequest(args[0])) {
      const startTime = args[0]?.startTime;
      const endTime = args[0]?.endTime;
      const fixture = await demoWorkloadEfficiency(startTime);
      return resolve({
        ...fixture,
        topItems: fixture.topItems?.map((item) => ({
          ...item,
          costImpactHistory: item.costImpactHistory?.filter((entry) =>
            dayjs(entry.timestamp).isBetween(dayjs(startTime), dayjs(endTime))
          ),
        })),
      });
    }
    return next();
  },
  getAllocationGroups: async ({ args, next, resolve }) => {
    if (getIsClusterDemoMode() || isSimulatedDemoClusterRequest(args[0])) {
      const fixture = await demoAllocationGroups();
      return resolve(fixture);
    }
    return next();
  },
  getAllocationGroupSummaries: async ({ args, next, resolve }) => {
    if (getIsClusterDemoMode() || isSimulatedDemoClusterRequest(args[0])) {
      const fixture = await demoAllocationGroupSummaries(args[0].groupId);
      return resolve(fixture);
    }
    return next();
  },
  getAllocationGroupTotalCosts: async ({ args, next, resolve }) => {
    if (getIsClusterDemoMode() || isSimulatedDemoClusterRequest(args[0])) {
      const fixture = await demoAllocationGroupTotalCosts(
        args[0].startTime,
        args[0].endTime
      );
      return resolve(fixture);
    }
    return next();
  },
  getAllocationGroupTimedSummaries: async ({ args, next, resolve }) => {
    if (getIsClusterDemoMode() || isSimulatedDemoClusterRequest(args[0])) {
      const fixture = await demoAllocationGroupTimedSummaries(
        args[0].startTime,
        args[0].endTime,
        args[0].groupId
      );
      return resolve(fixture);
    }
    return next();
  },
  getAllocationGroupWorkloads: async ({ args, next, resolve }) => {
    if (getIsClusterDemoMode() || isSimulatedDemoClusterRequest(args[0])) {
      const fixture = await demoAllocationGroupWorkloads(args[0]?.groupId);
      return resolve(fixture);
    }
    return next();
  },
  getAllocationGroupDataTransferSummary: async ({ args, next, resolve }) => {
    if (getIsClusterDemoMode() || isSimulatedDemoClusterRequest(args[0])) {
      const fixture = await demoAllocationGroupNetworkCostSummaries();
      return resolve(fixture);
    }
    return next();
  },
  getAllocationGroupDataTransferWorkloads: async ({ args, next, resolve }) => {
    if (getIsClusterDemoMode() || isSimulatedDemoClusterRequest(args[0])) {
      const fixture = await demoAllocationGroupNetworkCostWorkloads(
        args[0]?.groupId
      );
      return resolve(fixture);
    }
    return next();
  },
  getAllocationGroupEfficiencySummary: async ({ args, next, resolve }) => {
    if (getIsClusterDemoMode() || isSimulatedDemoClusterRequest(args[0])) {
      const fixture = await demoAllocationGroupsEfficiencySummary(
        args[0]?.startTime
      );
      return resolve(fixture);
    }
    return next();
  },
  getAllocationGroupWorkloadsEfficiency: async ({ args, next, resolve }) => {
    if (getIsClusterDemoMode() || isSimulatedDemoClusterRequest(args[0])) {
      const fixture = await demoAllocationGroupsWorkloadsEfficiency();
      return resolve(fixture);
    }
    return next();
  },
  createAllocationGroup: async ({ next }) => {
    // getIsDemoMode: org-level demo (classic or OP). Mutations have no clusterId.
    // isSimulatedDemoClusterRequest: request targets synthetic demo cluster id (reads).
    if (getIsDemoMode()) {
      return rejectDemoClusterRequest();
    }
    return next();
  },
  updateAllocationGroup: async ({ next }) => {
    if (getIsDemoMode()) {
      return rejectDemoClusterRequest();
    }
    return next();
  },
  deleteAllocationGroup: async ({ next }) => {
    if (getIsDemoMode()) {
      return rejectDemoClusterRequest();
    }
    return next();
  },
  getWorkloadEfficiencyDetails: async ({ args, next, resolve }) => {
    if (getIsClusterDemoMode() || isSimulatedDemoClusterRequest(args[0])) {
      const fixture = await demoWorkloadEfficiencyDetails(
        args[0]?.startTime,
        args[0]?.endTime
      );
      return resolve(fixture);
    }
    return next();
  },
  getOrganizationClustersSummary: async ({
    next,
    resolve,
    feature,
    endpoint,
  }) => {
    return handleOpDemoInterceptor({
      feature,
      endpoint,
      next,
      resolve,
      demoFixtureLoader: demoOrganizationClustersSummary,
    });
  },
  getClusterSummary: async ({ args, next, resolve }) => {
    if (getIsClusterDemoMode() || isSimulatedDemoClusterRequest(args[0])) {
      const fixture = await demoOrganizationClusterSummary();
      return resolve(fixture);
    }
    return next();
  },
  getResourceUsage: async ({ args, next, resolve, feature, endpoint }) => {
    return handleOpDemoInterceptor({
      feature,
      endpoint,
      next,
      resolve,
      args,
      demoClusterFallback: (args) =>
        demoResourceUsage(
          args[0]?.startTime,
          args[0]?.endTime,
          args[0]?.stepSeconds ?? 600
        ),
    });
  },
  getUnscheduledPods: async ({ args, next, resolve }) => {
    if (getIsClusterDemoMode() || isSimulatedDemoClusterRequest(args[0])) {
      const fixture = await demoOrganizationClusterUnscheduledPods();
      return resolve(fixture);
    }
    return next();
  },
  getDataTransferCost: async ({ args, next, resolve }) => {
    if (getIsClusterDemoMode() || isSimulatedDemoClusterRequest(args[0])) {
      const fixture = await demoDataTransferCost(
        args[0]?.startTime,
        args[0]?.endTime
      );
      return resolve(fixture);
    }
    return next();
  },
  getClusterDataTransferSummary: async ({ args, next, resolve }) => {
    if (getIsClusterDemoMode()) {
      const fixture = await demoDataTransferCostSummary(
        args[0]?.startTime,
        args[0]?.endTime
      );
      return resolve(fixture);
    }
    return next();
  },
  getWorkloadDataTransferCost: async ({ args, next, resolve }) => {
    if (getIsClusterDemoMode() || isSimulatedDemoClusterRequest(args[0])) {
      const listFixture = await demoWorkloadDataTransferCost(args[0]?.endTime);
      const fixture = {
        clusterId: listFixture.clusterId,
        egressdStatus: listFixture.egressdStatus,
        item: listFixture.items?.find(
          (i) =>
            i.workloadName === args[0]?.workloadName &&
            i.namespace === args[0]?.namespace
        ),
      };

      return resolve(fixture);
    }
    return next();
  },
  getClusterEfficiencyDetails: async ({ args, next, resolve }) => {
    if (getIsClusterDemoMode() || isSimulatedDemoClusterRequest(args[0])) {
      const fixture = await demoClusterEfficiencyDetails(
        args[0]?.startTime,
        args[0]?.endTime,
        args[0]?.stepSeconds
      );
      return resolve(fixture);
    }
    return next();
  },
  getSavingsProgress: async ({ args, next, resolve }) => {
    if (getIsClusterDemoMode() || isSimulatedDemoClusterRequest(args[0])) {
      const fixture = await demoSavingsProgress(
        args[0]?.startTime,
        args[0]?.endTime
      );
      return resolve(fixture);
    }
    return next();
  },
  getWorkloadTrafficDestinations: async ({ args, next, resolve }) => {
    if (getIsClusterDemoMode() || isSimulatedDemoClusterRequest(args[0])) {
      const fixture = await demoWorkloadTrafficDestinations();
      return resolve(fixture);
    }
    return next();
  },
  getWorkloadTrafficDestinationsHistory: async ({ args, next, resolve }) => {
    if (getIsClusterDemoMode() || isSimulatedDemoClusterRequest(args[0])) {
      const fixture = await demoWorkloadTrafficDestinationsHistory(
        args[0]?.startTime,
        args[0]?.endTime
      );
      return resolve(fixture);
    }
    return next();
  },
  getWorkloadsGpuUtilizationSummary: async ({ args, next, resolve }) => {
    if (getIsClusterDemoMode() || isSimulatedDemoClusterRequest(args[0])) {
      const fixture = await demoWorkloadsGpuUtilizationSummary();
      return resolve(fixture);
    }
    return next();
  },
  getWorkloadsWastedGPUCostImpact: async ({ args, next, resolve }) => {
    if (getIsClusterDemoMode() || isSimulatedDemoClusterRequest(args[0])) {
      const fixture = await demoWorkloadsGpuWasteCostImpact(
        args[0]?.startTime,
        args[0]?.endTime
      );
      return resolve(fixture);
    }
    return next();
  },
  getReportCapabilities: async ({ args, next, resolve }) => {
    if (getIsClusterDemoMode() || isSimulatedDemoClusterRequest(args[0])) {
      const fixture = await demoReportCapabilities();
      return resolve(fixture);
    }
    return next();
  },
  getClusterWorkloadEventMetrics: async ({ args, next, resolve }) => {
    if (getIsClusterDemoMode() || isSimulatedDemoClusterRequest(args[0])) {
      return resolve({ series: [] });
    }
    return next();
  },
  getWorkloadGpuUtilizationSummary: async ({ args, next, resolve }) => {
    if (getIsClusterDemoMode() || isSimulatedDemoClusterRequest(args[0])) {
      const fixture = await demoWorkloadGpuUtilizationSummary(
        args[0]?.startTime,
        args[0]?.endTime
      );
      return resolve(fixture);
    }
    return next();
  },
  getClusterAnomalies: async ({ args, next, resolve }) => {
    if (getIsClusterDemoMode() || isSimulatedDemoClusterRequest(args[0])) {
      const fixture = await demoClusterAnomalies();
      return resolve({
        ...fixture,
        anomalies: fixture.anomalies?.filter((entry) =>
          dayjs(entry.timestamp).isBetween(
            dayjs(args[0]?.startTime),
            dayjs(args[0]?.endTime)
          )
        ),
      });
    }
    return next();
  },
  getOrganizationClustersEfficiencySummary: async ({ next, resolve }) => {
    if (getIsClusterDemoMode()) {
      const fixture = await demoClustersEfficiencySummary();
      return resolve(fixture);
    }
    return next();
  },
  getOrganizationClustersTimedEfficiency: async ({
    next,
    resolve,
    feature,
    endpoint,
  }) => {
    return handleOpDemoInterceptor({
      feature,
      endpoint,
      next,
      resolve,
      demoFixtureLoader: demoClustersTimedEfficiency,
    });
  },
  getOrganizationClustersEfficiencyByCluster: async ({ next, resolve }) => {
    if (getIsClusterDemoMode()) {
      const fixture = await demoClustersEfficiencySummaryByCluster();
      return resolve(fixture);
    }
    return next();
  },
  getClusterNodeTemplatesReport: async ({ args, next, resolve }) => {
    if (getIsClusterDemoMode() || isSimulatedDemoClusterRequest(args[0])) {
      const fixture = await demoNodeTemplatesReport();
      return resolve(fixture);
    }
    return next();
  },
  getClusterNodesStorageReport: async ({ args, next, resolve }) => {
    if (getIsClusterDemoMode() || isSimulatedDemoClusterRequest(args[0])) {
      const fixture = await demoNodeStorageReport();
      return resolve(fixture);
    }
    return next();
  },
  getContainerLiveMigrations: async ({ args, next, resolve }) => {
    if (getIsClusterDemoMode() || isSimulatedDemoClusterRequest(args[0])) {
      return resolve({
        items: [],
        totalCount: 0,
        nextPageCursor: '',
      });
    }
    return next();
  },
  getContainerLiveMigrationSummary: async ({ args, next, resolve }) => {
    if (getIsClusterDemoMode() || isSimulatedDemoClusterRequest(args[0])) {
      return resolve({
        items: [],
        summary: {
          totalMigrations: '0',
          successfulMigrations: '0',
          failedMigrations: '0',
          averageMigrationRate: 0,
        },
      });
    }
    return next();
  },
  getContainerLiveMigrationDetail: async ({ args, next, resolve }) => {
    if (getIsClusterDemoMode() || isSimulatedDemoClusterRequest(args[0])) {
      return resolve({
        migration: {
          id: args[0]?.migrationId,
        },
        timeline: [],
        migrationRaw: '',
        sourceNode: '',
        destinationNode: '',
      });
    }
    return next();
  },
};
